/* eslint-disable no-underscore-dangle */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getLocationConfig, getLocation } from '../api';
import {
  IDLE, PENDING, SUCCEEDED, FAILED,
} from '../constants/asyncLoadingStatuses';
import { DEFAULT_LOCATION_SLUG, setInitialLocationAngularSideEffects, setSelectedLocationAngularSideEffects } from './helpers/resyLocationsSideEffects';
import getDeferred from './helpers/getDeferred';
import Logger from '../utils/logger';

const DEFAULT_COORDINATES = { // ny
  lat: 40.7643304,
  long: -73.9772046,
};

// ensure a singleton deferred
if (!window.resyLocationsLoadConfig) {
  const [promise, resolve, reject] = getDeferred();
  window.resyLocationsLoadConfig = { promise, resolve, reject };
}

const getLatLong = (coordinates) => {
  if (coordinates.lat && coordinates.long) {
    const { lat, long } = coordinates;
    return { lat, long };
  }
  return DEFAULT_COORDINATES;
};

const sortWebLocations = (a, b) => {
  const nameA = a.name.toLowerCase();
  const nameB = b.name.toLowerCase();
  if (nameA < nameB) { return -1; }
  if (nameA > nameB) { return 1; }
  return 0;
};

const loadResyLocationsConfigThunk = createAsyncThunk(
  'resyLocations/loadResyLocationsConfig',
  async (coordinates, { rejectWithValue }) => {
    try {
      const response = await getLocationConfig(getLatLong(coordinates));
      return response;
    } catch (error) {
      const message = error?.data?.message?.venue_id;
      const status = error?.status;
      const formattedError = { status, data: { message } };
      return rejectWithValue(formattedError);
    }
  }
);

const loadResyLocationThunk = createAsyncThunk(
  'resyLocations/loadResyLocationThunk',
  async (coordinates, { rejectWithValue }) => {
    const coordinatesData = { lat: coordinates.lat, long: coordinates.lng }; // note: .lng is not a typo
    try {
      const response = await getLocation(coordinatesData);
      return response;
    } catch (error) {
      const message = error?.data?.message?.venue_id;
      const status = error?.status;
      const formattedError = { status, data: { message } };
      return rejectWithValue(formattedError);
    }
  }
);

const resyLocationsSlice = createSlice({
  name: 'resyLocations',
  initialState: {
    configStatus: IDLE,
    all: [],
    byCode: {},
    bySlug: {},
    nearest: null,
    rgaCityCode: null,
    rgaCityName: null,
    previouslySelected: null,
    selected: null,
    slugs: {},
    web: [],
    nav: [],
    wineRegions: [],
    marketLocationStatus: IDLE,
    marketLocation: null,
  },
  reducers: {
    setSelectedLocation: (state, action) => {
      state.previouslySelected = state.selected;
      state.selected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadResyLocationsConfigThunk.pending, (state) => {
      state.configStatus = PENDING;
    });
    builder.addCase(loadResyLocationsConfigThunk.fulfilled, (state, action) => {
      const { payload: data } = action;
      state.all = data;
      data.forEach((location) => {
        state.byCode[location.code] = location;
        state.bySlug[location.url_slug] = location;
      });
      state.nearest = data.find((location) => location.show_on_web)?.url_slug || null;
      const { code = null, name = null } = data.find((location) => location.has_rga_venues) || {};
      state.rgaCityCode = code;
      state.rgaCityName = name;
      state.web = data
        .filter((location) => (location.show_on_web === 1))
        .sort(sortWebLocations);
      state.nav = state.web.filter((location) => (location.show_in_display));
      state.wineRegions = data
        .filter((location) => location.is_wine_region)
        .sort(sortWebLocations);
      state.configStatus = SUCCEEDED;
      window.resyLocationsLoadConfig.resolve();
    });
    builder.addCase(loadResyLocationsConfigThunk.rejected, (state, action) => {
      const { payload: { data: { message } } } = action;
      state.configStatus = FAILED;
      Logger.error(`[ERROR] loadResyLocationsConfigThunk, ${message}`);
      window.resyLocationsLoadConfig.reject();
    });

    builder.addCase(loadResyLocationThunk.pending, (state) => {
      state.marketLocationStatus = PENDING;
    });
    builder.addCase(loadResyLocationThunk.fulfilled, (state, action) => {
      const { payload: data } = action;
      state.marketLocation = data?.in?.location || null;
      state.marketLocationStatus = SUCCEEDED;
    });
    builder.addCase(loadResyLocationThunk.rejected, (state, action) => {
      const { payload: { data: { message } } } = action;
      state.marketLocationStatus = FAILED;
      Logger.error(`[ERROR] loadResyLocationThunk, ${message}`);
    });
  },
});

const { setSelectedLocation } = resyLocationsSlice.actions;

const setSelectedLocationThunk = (params) => (dispatch) => {
  const { location } = params;
  dispatch(setSelectedLocation(location));
  setSelectedLocationAngularSideEffects(location);
};

const resetLocationThunk = () => (dispatch, getState) => {
  const { resyLocations: { web } } = getState();
  const defaultLocation = web.find((location) => location.url_slug === DEFAULT_LOCATION_SLUG);
  dispatch(setSelectedLocationThunk({ location: defaultLocation }));
};

const initResyLocationsConfigThunk = (params) => (dispatch, getState) => {
  const {
    coordinates, $rootScope, $route, $routeParams,
  } = params;
  return dispatch(loadResyLocationsConfigThunk(coordinates))
    .then(() => {
      const { resyLocations } = getState();
      const defaultLocation = setInitialLocationAngularSideEffects(
        resyLocations, $rootScope, $route, $routeParams
      );
      dispatch(setSelectedLocationThunk({ location: defaultLocation }));
      return resyLocations.web;
    })
    .catch((error) => {
      Logger.error(`[ERROR] initResyLocationsConfigThunk: ${error}`);
    });
};

const getResyLocationsSelectedThunk = () => (dispatch, getState) => {
  const { resyLocations: { selected } } = getState();
  return selected;
};

const getResyLocationsByCodeThunk = (locationCode) => (dispatch, getState) => window.resyLocationsLoadConfig.promise
  .then(() => {
    const { resyLocations } = getState();
    const { configStatus, byCode } = resyLocations;
    let result = {};
    if (configStatus === SUCCEEDED) {
      result = byCode[locationCode];
    }
    return result;
  });

const getResyLocationsBySlugThunk = (locationCode) => (dispatch, getState) => window.resyLocationsLoadConfig.promise
  .then(() => {
    const { resyLocations } = getState();
    const { configStatus, bySlug } = resyLocations;
    let result = {};
    if (configStatus === SUCCEEDED) {
      result = bySlug[locationCode];
    }
    return result;
  });

const getResyLocationsThunk = (type = 'web') => (dispatch, getState) => window.resyLocationsLoadConfig.promise
  .then(() => {
    const { resyLocations } = getState();
    const { configStatus } = resyLocations;
    let result = {};
    if (configStatus === SUCCEEDED) {
      result = resyLocations[type];
    }
    return result;
  });

const getMarketLocationThunk = (params) => (dispatch, getState) => {
  const { coordinates } = params;
  return dispatch(loadResyLocationThunk(coordinates))
    .then(() => {
      const { resyLocations } = getState();
      return resyLocations.marketLocation;
    })
    .catch((error) => {
      Logger.error(`[ERROR] getMarketLocationThunk: ${error}`);
    });
};

export {
  DEFAULT_COORDINATES,
  getMarketLocationThunk,
  getResyLocationsByCodeThunk,
  getResyLocationsSelectedThunk,
  getResyLocationsBySlugThunk,
  getResyLocationsThunk,
  initResyLocationsConfigThunk,
  loadResyLocationThunk,
  loadResyLocationsConfigThunk,
  resetLocationThunk,
  setSelectedLocationThunk,
};

export default resyLocationsSlice.reducer;
